/**
 * The dispute surface — a binding decision contested on the record (ADR-0076; ADR-0069 A6).
 *
 * Two pieces, the same split as the challenge surface (`Challenges.tsx`):
 *   • {@link DisputeAction} — on EACH row of the Decision Record timeline. A dispute is about one
 *     decision, so the affordance sits on that decision rather than in a general box.
 *   • {@link DisputeLedgerCard} — every dispute and its answer, open ones first.
 *
 * ## Who sees what (the server enforces every one of these; this is the courtesy, the 403 is the control)
 *
 *   • Filing: owner, admin or member. A viewer (the role a client holds) sees the ledger and no button
 *     (ADR-0076 Q1).
 *   • Answering: owner or admin, and NEVER the person who filed that dispute. Withheld here by user id
 *     (`disputedByUserId`), never by name: two people can share a name.
 *   • The decider MAY answer a dispute of their own decision (Q2). The ledger says so on the answer.
 *
 * ## 🔴 Accepting a dispute does not reverse the decision
 *
 * The answer route never touches the decision (ADR-0076 D3). A reader who sees "Accepted" beside a
 * release would reasonably think the release was undone, so the ledger says in words that it was not.
 */

import { useState } from 'react';
import {
  answerDispute,
  fileDispute,
  type DecisionDispute,
  type DecisionRecord,
  type DisputeLedger,
  type DisputeResolution,
  type ProjectDocument,
} from '../api.js';
import { timestamp } from '../format.js';

/** Everything the dispute surface needs from the project page. */
export interface DisputesContext {
  /** How the page was addressed — a project number or a pursuit's record id. */
  code: string;
  /** Null when the ledger could not be read. Never stood in for by an empty ledger. */
  ledger: DisputeLedger | null;
  /** May this caller file a dispute (owner/admin/member)? */
  canFile: boolean;
  /** May this caller answer one (owner/admin)? The filer of a dispute still may not answer it. */
  canAnswer: boolean;
  /** The signed-in user's id, to withhold the answer form from a dispute's own filer. */
  userId: string | null;
  /** The record's documents, offered as the optional supporting document and used to name it. */
  documents: ProjectDocument[];
  /** The timeline, so a ledger row can say which decision it is about in the timeline's own words. */
  decisions: DecisionRecord[];
  /** The timeline's one-line description of a decision. */
  describe: (d: DecisionRecord) => string;
  /** Re-read the record after a change. */
  reload: () => Promise<void>;
}

/**
 * The longest claim or reasoning the routes accept (`maxLength: 4000` in both body schemas). Past it the
 * server answers 400 with no plain reason, and the reader would be told to "try again" at something a
 * retry can never fix, so the box stops there instead.
 */
const MAX_TEXT = 4000;

const RESOLUTION_VIEW: Record<DisputeResolution, { label: string; tone: string }> = {
  accepted: { label: 'Accepted', tone: 'fixable' },
  partial: { label: 'Partly accepted', tone: 'partial' },
  rejected: { label: 'Not accepted', tone: 'structural' },
};

/** What a decision kind is called when the decision itself is not on the timeline we hold. */
const KIND_LABEL: Record<DecisionRecord['kind'], string> = {
  intake: 'Record opened',
  transition: 'State change',
  crp_approval: 'Spend released',
  variance_clearance: 'Variance cleared',
  promotion: 'Promoted to a GGP project',
  stop_attestation: 'Protected-stop decision',
  basis_lock: 'Basis locked',
};

/**
 * WHO made a decision, in words. ONE definition, read by the timeline and the dispute ledger, so the
 * two can never describe the same decision differently.
 *
 * ⚠️ Three states, not two. A name is a person. No name but a user id is a person whose decision was
 * recorded before names were kept (migration 0031 left those rows unnamed): "Name not recorded". No
 * name and no id is the Engine: a system transition has no human, and inventing one would be the
 * confidently wrong value this exists to avoid. The ledger once said "the Engine" for the middle case.
 */
export function deciderLabel(name: string | null | undefined, userId: string | null): string {
  if (name) return name;
  return userId ? 'Name not recorded' : 'the Engine';
}

/**
 * The decider line on a ledger row. The ledger carries the decider's name but not their id, so the id
 * comes from the timeline. When the decision is not on the timeline we hold (its read failed), a null
 * name could be either a person or the Engine, so no decider is named at all.
 */
export function disputedDecider(ctx: DisputesContext, d: DecisionDispute): string | null {
  if (d.decidedBy) return d.decidedBy;
  const decision = ctx.decisions.find((x) => x.id === d.decisionId);
  return decision ? deciderLabel(decision.actorName, decision.actorUserId) : null;
}

/** Open disputes against one decision. Exported for a direct unit test. */
export function openDisputesFor(ledger: DisputeLedger | null, decisionId: string): number {
  if (!ledger) return 0;
  return ledger.disputes.filter((d) => d.decisionId === decisionId && !d.answer).length;
}

/**
 * Which decision a ledger row is about, in the timeline's words. Falls back to the kind when the
 * decision is not in the timeline we hold, rather than showing an id.
 */
export function disputedDecisionLabel(ctx: DisputesContext, d: DecisionDispute): string {
  const decision = ctx.decisions.find((x) => x.id === d.decisionId);
  return decision ? ctx.describe(decision) : (KIND_LABEL[d.decisionKind] ?? 'A decision');
}

/**
 * Whether THIS caller may answer THIS dispute. Exported because it is the rule that decides whether a
 * button appears: owner/admin, an open dispute, and not the person who filed it.
 *
 * ⚠️ A dispute whose filer is unknown (`disputedByUserId` null) is answerable. The route checks the same
 * way, and refusing it here would leave a dispute nobody could ever answer.
 */
export function mayAnswer(ctx: DisputesContext, d: DecisionDispute): boolean {
  if (!ctx.canAnswer || d.answer) return false;
  return !(d.disputedByUserId !== null && d.disputedByUserId === ctx.userId);
}

/** The record's documents a dispute may cite: one entry per document, withdrawn ones left out. */
export function citableDocuments(documents: ProjectDocument[]): ProjectDocument[] {
  const seen = new Set<string>();
  const out: ProjectDocument[] = [];
  for (const doc of documents) {
    if (doc.withdrawnAt !== null || seen.has(doc.documentId)) continue;
    seen.add(doc.documentId);
    out.push(doc);
  }
  return out;
}

/** The name of a cited document, or a plain stand-in when the record no longer lists it. */
export function documentName(documents: ProjectDocument[], documentId: string): string {
  return documents.find((d) => d.documentId === documentId)?.filename ?? 'a document on the record';
}

/** Whether an answer can be sent as written: reasoning is required at the route and the database. */
export function canSubmitDisputeAnswer(reasoning: string): boolean {
  return reasoning.trim().length > 0;
}

// ── On each timeline row ────────────────────────────────────────────────────────────────────────────

export function DisputeAction({
  ctx,
  decision,
}: {
  ctx: DisputesContext;
  decision: DecisionRecord;
}) {
  const [open, setOpen] = useState(false);
  const [claim, setClaim] = useState('');
  const [docId, setDocId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openCount = openDisputesFor(ctx.ledger, decision.id);
  const countChip =
    openCount > 0 ? (
      <span className="chal__count">
        {openCount} open {openCount === 1 ? 'dispute' : 'disputes'}
      </span>
    ) : null;

  // A viewer files nothing; they still see that a decision is under dispute.
  if (!ctx.canFile) return countChip;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await fileDispute(ctx.code, decision.id, {
        claim,
        supportingDocumentId: docId === '' ? null : docId,
      });
      setClaim('');
      setDocId('');
      setOpen(false);
      await ctx.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We couldn’t file that dispute just now.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <span className="tl__dispute">
        <button type="button" className="chal__open" onClick={() => setOpen(true)}>
          Dispute this decision
        </button>
        {countChip}
      </span>
    );
  }

  const docs = citableDocuments(ctx.documents);
  return (
    <div className="chal__form">
      <label className="chal__label" htmlFor={`dispute-claim-${decision.id}`}>
        What’s wrong with this decision?
      </label>
      <textarea
        id={`dispute-claim-${decision.id}`}
        className="chal__textarea"
        rows={3}
        value={claim}
        maxLength={MAX_TEXT}
        placeholder="For example: the release went ahead with the title exposure still unpriced."
        onChange={(e) => setClaim(e.target.value)}
      />
      {docs.length > 0 && (
        <>
          <label className="chal__label" htmlFor={`dispute-doc-${decision.id}`}>
            Supporting document (optional)
          </label>
          <select
            id={`dispute-doc-${decision.id}`}
            className="chal__input"
            value={docId}
            onChange={(e) => setDocId(e.target.value)}
          >
            <option value="">None</option>
            {docs.map((d) => (
              <option key={d.documentId} value={d.documentId}>
                {d.filename}
              </option>
            ))}
          </select>
          <p className="chal__hint">
            Cite a document from this record if you have one. It strengthens the record.
          </p>
        </>
      )}
      {error && <p className="chal__error">{error}</p>}
      <div className="chal__actions">
        <button
          type="button"
          className="chal__send"
          disabled={busy || claim.trim().length === 0}
          onClick={() => void send()}
        >
          {busy ? 'Filing…' : 'File the dispute'}
        </button>
        <button type="button" className="chal__cancel" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── The ledger ──────────────────────────────────────────────────────────────────────────────────────

export function DisputeLedgerCard({ ctx }: { ctx: DisputesContext }) {
  const ledger = ctx.ledger;
  return (
    <section className="card chal">
      <h3 className="card__title">
        Disputes
        {ledger && ledger.openCount > 0 && (
          <span className="chip chip--partial chal__badge">{ledger.openCount} open</span>
        )}
      </h3>
      {ledger === null ? (
        <p className="card__prose">
          The dispute list didn’t load, so we can’t say whether anything here is disputed. Reload
          the page to try again.
        </p>
      ) : ledger.disputes.length === 0 ? (
        <p className="card__prose">
          {!ctx.canFile
            ? 'Nothing on this record has been disputed. When a decision is disputed, the dispute and its answer are listed here.'
            : ctx.decisions.length === 0
              ? 'Nothing on this record has been disputed. Once the record has decisions, each one can be disputed from its row in the timeline above.'
              : 'Nothing on this record has been disputed. Any decision in the timeline above can be: use “Dispute this decision” on its row, and the dispute and its answer stay on the record.'}
        </p>
      ) : (
        <ol className="chal__list">
          {ledger.disputes.map((d) => (
            <DisputeRow key={d.id} ctx={ctx} dispute={d} />
          ))}
        </ol>
      )}
    </section>
  );
}

function DisputeRow({ ctx, dispute }: { ctx: DisputesContext; dispute: DecisionDispute }) {
  const filedByMe = dispute.disputedByUserId !== null && dispute.disputedByUserId === ctx.userId;
  return (
    <li className={`chal__row chal__row--${dispute.status}`}>
      <div className="chal__head">
        <span className="chal__target">{disputedDecisionLabel(ctx, dispute)}</span>
        {dispute.answer ? (
          <span
            className={`chip chip--${RESOLUTION_VIEW[dispute.answer.resolution].tone} chal__chip`}
          >
            {RESOLUTION_VIEW[dispute.answer.resolution].label}
          </span>
        ) : (
          <span className="chip chip--partial chal__chip">Open</span>
        )}
      </div>
      <p className="chal__meta muted">
        {(() => {
          const decider = disputedDecider(ctx, dispute);
          return decider
            ? `Decided by ${decider} · ${timestamp(dispute.decidedAt)}`
            : `Decided ${timestamp(dispute.decidedAt)}`;
        })()}
      </p>
      <p className="chal__claim">{dispute.claim}</p>
      <p className="chal__meta muted">
        Disputed by {dispute.disputedByName} · {timestamp(dispute.createdAt)}
        {dispute.supportingDocumentId
          ? ` · cites ${documentName(ctx.documents, dispute.supportingDocumentId)}`
          : ''}
      </p>

      {dispute.answer ? (
        <div className="chal__answer">
          <p className="chal__reason">{dispute.answer.reasoning}</p>
          <p className="chal__meta muted">
            Answered by {dispute.answer.answeredByName} · {timestamp(dispute.answer.answeredAt)}
          </p>
          {dispute.answer.answeredOwnDecision && (
            <p className="chal__rescore dispute__own">
              {dispute.answer.answeredByName} made the decision this dispute is about, and answered
              it.
            </p>
          )}
          {dispute.answer.resolution !== 'rejected' && (
            <p className="chal__hint">
              The decision still stands. Undoing it would be a separate decision, recorded on its
              own.
            </p>
          )}
        </div>
      ) : mayAnswer(ctx, dispute) ? (
        <DisputeAnswerForm ctx={ctx} dispute={dispute} />
      ) : (
        <p className="chal__meta muted">
          {filedByMe && ctx.canAnswer
            ? 'You filed this dispute, so another owner or admin answers it.'
            : 'Waiting on an answer from an owner or admin.'}
        </p>
      )}
    </li>
  );
}

function DisputeAnswerForm({ ctx, dispute }: { ctx: DisputesContext; dispute: DecisionDispute }) {
  const [resolution, setResolution] = useState<DisputeResolution>('accepted');
  const [reasoning, setReasoning] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The decider may answer (ADR-0076 Q2). Said before they do, so the flag on the record is no surprise.
  const decider = ctx.decisions.find((x) => x.id === dispute.decisionId)?.actorUserId ?? null;
  const ownDecision = decider !== null && decider === ctx.userId;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await answerDispute(ctx.code, dispute.decisionId, dispute.id, { resolution, reasoning });
      await ctx.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We couldn’t record that answer just now.');
      // A refusal can mean the record moved under us (someone else answered first, a 409), so re-read
      // it: the reader then sees the answer that won rather than an open form beside "already answered".
      await ctx.reload();
    } finally {
      // In `finally`: `reload` keeps the old ledger when a refresh fails, and a form left on
      // "Recording…" after a saved answer would never come back.
      setBusy(false);
    }
  }

  return (
    <div className="chal__form chal__form--answer">
      <div className="chal__choices" role="group" aria-label="How are you answering this dispute?">
        {(['accepted', 'partial', 'rejected'] as DisputeResolution[]).map((r) => (
          <button
            key={r}
            type="button"
            className={`chal__choice${resolution === r ? ' chal__choice--on' : ''}`}
            aria-pressed={resolution === r}
            onClick={() => setResolution(r)}
          >
            {RESOLUTION_VIEW[r].label}
          </button>
        ))}
      </div>
      <label className="chal__label" htmlFor={`dispute-reason-${dispute.id}`}>
        Why? (required)
      </label>
      <textarea
        id={`dispute-reason-${dispute.id}`}
        className="chal__textarea"
        rows={3}
        value={reasoning}
        maxLength={MAX_TEXT}
        placeholder="Say what you checked and what you concluded."
        onChange={(e) => setReasoning(e.target.value)}
      />
      {ownDecision && (
        <p className="chal__hint">
          You made this decision. You can still answer the dispute, and the record will show that
          the decider answered it.
        </p>
      )}
      {resolution !== 'rejected' && (
        <p className="chal__hint">
          Accepting a dispute doesn’t undo the decision. Undoing it is a separate decision, made and
          recorded on its own.
        </p>
      )}
      {error && <p className="chal__error">{error}</p>}
      <div className="chal__actions">
        <button
          type="button"
          className="chal__send"
          disabled={busy || !canSubmitDisputeAnswer(reasoning)}
          onClick={() => void send()}
        >
          {busy ? 'Recording…' : 'Record the answer'}
        </button>
      </div>
    </div>
  );
}
